import { TFunction } from "i18next";
import isEmpty from "lodash/isEmpty";
import * as accessChecks from "components/common/Auth/accessChecks";
import { TUserContextType } from "context/UserContext";
import { TFeatureFlagsMap } from "models/featureFlags.models";
import { Path } from "utils/constants/paths.constants";
import { includeInArray } from "utils/helpers";
import {
  CheckSquareBrokenIcon as TasksIcon,
  FolderIcon as ProjectsIcon,
  HomeIcon,
  SettingsIcon,
} from "assets/icons";

const getAdminChildNavigationItems = (
  t: TFunction,
  userContext: TUserContextType,
  featureFlags: TFeatureFlagsMap,
) => [
  ...includeInArray(
    {
      key: "admin-user-management",
      label: t(
        "notificationsMainNavigationList.navItemData.adminOptions.userManagement",
      ),
      href: Path.AdminUserManagement,
    },
    accessChecks.canAccessUserManagement({ userContext, featureFlags }),
  ),
  ...includeInArray(
    {
      key: "admin-tenants",
      label: t(
        "notificationsMainNavigationList.navItemData.adminOptions.tenants",
      ),
      href: Path.AdminTenants,
    },
    accessChecks.canAccessCCS({ userContext, featureFlags }),
  ),
  ...includeInArray(
    {
      key: "admin-feature-flags",
      label: t(
        "notificationsMainNavigationList.navItemData.adminOptions.featureFlags",
      ),
      href: Path.AdminFeatureFlags,
    },
    accessChecks.canAccessCCS({ userContext, featureFlags }),
  ),
];

export const getAdminNavigationItems = (
  t: TFunction,
  userContext: TUserContextType,
  featureFlags: TFeatureFlagsMap,
) => {
  const childAdminNavigationItems = getAdminChildNavigationItems(
    t,
    userContext,
    featureFlags,
  );

  return accessChecks.canAccessAdminPages({ userContext, featureFlags }) &&
    !isEmpty(childAdminNavigationItems)
    ? [
        {
          key: "admin",
          label: t("notificationsMainNavigationList.navItemData.admin"),
          icon: <SettingsIcon aria-label={t("iconTitles.admin")} />,
          href: Path.Admin,
          items: childAdminNavigationItems,
          disabled: false,
        },
      ]
    : [];
};

export const getNonAdminNavigationItems = (
  t: TFunction,
  userContext: TUserContextType,
  featureFlags: TFeatureFlagsMap,
) => [
  {
    key: "home",
    label: t("notificationsMainNavigationList.navItemData.home"),
    icon: <HomeIcon aria-label={t("iconTitles.home")} />,
    href: Path.HomePage,
    items: [],
    disabled: false,
  },
  ...includeInArray(
    [
      {
        key: "tasks",
        label: t("notificationsMainNavigationList.navItemData.tasks"),
        icon: <TasksIcon aria-label={t("iconTitles.tasks")} />,
        href: Path.Tasks,
        items: [],
        disabled: false,
      },
      {
        key: "projects",
        label: t("notificationsMainNavigationList.navItemData.projects"),
        icon: <ProjectsIcon aria-label={t("iconTitles.projects")} />,
        href: Path.Projects,
        items: [],
        disabled: false,
      },
    ],
    accessChecks.canAccessApp({ userContext, featureFlags }),
  ).flat(),
];
